import { Box, Typography } from "@mui/material"
import { faClock, faUserTie, faBuilding, faLocationDot } from "@fortawesome/pro-regular-svg-icons"
import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { tailwindPalette } from "../../../../../theme/tailwindPalette"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { MeetingOrderOrganizer } from "../../meetings/meetingTypes"
import { faInfoCircle } from "@fortawesome/pro-light-svg-icons"
import { getHostOrganizer, getHostOrganizerName } from "../meetigs/helpers"
import { StandardDialog } from "../../../../../../shared/components/dialogs/StandardDialog"
import { useState } from "react"

const useStyles = makeStyles()(() => ({
    container: {
        display: "flex",
        flexDirection: "column",
        gap: ".5rem",
    },
    detailRow: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
    },
    icon: {
        color: tailwindPalette.slate[400],
        width: "1rem",
    },
    infoButton: {
        cursor: "pointer",
        marginLeft: ".5rem",
        color: "#64748B",
        ":hover": {
            color: "#94A3B8",
        },
    },
    userDialog: {
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: ".5rem",
        rowGap: ".5rem",
        alignItems: "start",
        padding: "1rem 2rem 2rem",
    },
    userDialogLabel: {
        color: tailwindPalette.slate[500],
    },
}))

type MeetingDetailsProps = {
    time?: string
    location?: string
    companyName?: string
    organizers?: Array<MeetingOrderOrganizer>
}

export function MeetingDetails({ time, location, companyName, organizers }: MeetingDetailsProps) {
    const { classes } = useStyles()
    const [isUserDetailsDialog, openDialog] = useState(false)

    const hostOrganizer = getHostOrganizer(
        organizers?.map((organizer) => ({
            ...organizer,
            type: organizer.organizerType,
        })),
    )

    return (
        <Box className={classes.container}>
            <Box className={classes.detailRow}>
                <FontAwesomeIcon icon={faUserTie} className={classes.icon} />
                <Typography>
                    <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-host">
                        <Typography component="span" variant="subtitle1">
                            Host:
                        </Typography>
                    </LocalizedStrict>{" "}
                    {getHostOrganizerName(
                        organizers?.map((organizer) => ({
                            name: organizer.name,
                            type: organizer.organizerType,
                            email: organizer.email,
                        })),
                    )}
                    <FontAwesomeIcon
                        onClick={(e) => {
                            openDialog(true)
                        }}
                        className={classes.infoButton}
                        icon={faInfoCircle}
                    />
                </Typography>
            </Box>
            <StandardDialog
                open={isUserDetailsDialog}
                onClose={() => openDialog(false)}
                maxWidthProp="sm"
                dialogTitle={
                    <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-title">
                        <>User details</>
                    </LocalizedStrict>
                }
            >
                <Box className={classes.userDialog}>
                    {hostOrganizer?.name && (
                        <>
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-name">
                                <Typography className={classes.userDialogLabel}>Name:</Typography>
                            </LocalizedStrict>
                            <Typography>{hostOrganizer.name}</Typography>
                        </>
                    )}
                    {hostOrganizer?.company?.name && (
                        <>
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-company-name">
                                <Typography className={classes.userDialogLabel}>Company:</Typography>
                            </LocalizedStrict>
                            <Typography>{hostOrganizer.company.name}</Typography>
                        </>
                    )}
                    {hostOrganizer?.department?.name && (
                        <>
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-department-name">
                                <Typography className={classes.userDialogLabel}>Department:</Typography>
                            </LocalizedStrict>
                            <Typography>{hostOrganizer.department.name}</Typography>
                        </>
                    )}
                    {hostOrganizer?.email && (
                        <>
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-email">
                                <Typography className={classes.userDialogLabel}>E-mail:</Typography>
                            </LocalizedStrict>
                            <Typography>{hostOrganizer.email}</Typography>
                        </>
                    )}
                    {hostOrganizer?.businessPhone && (
                        <>
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-business-phone">
                                <Typography className={classes.userDialogLabel}>Phone number:</Typography>
                            </LocalizedStrict>
                            <Typography>{hostOrganizer.businessPhone}</Typography>
                        </>
                    )}
                    {hostOrganizer?.mobilePhone && (
                        <>
                            <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-user-details-dialog-mobile-phone">
                                <Typography className={classes.userDialogLabel}>Mobile number:</Typography>
                            </LocalizedStrict>
                            <Typography>{hostOrganizer.mobilePhone}</Typography>
                        </>
                    )}
                </Box>
            </StandardDialog>
            <Box className={classes.detailRow}>
                <FontAwesomeIcon icon={faBuilding} className={classes.icon} />
                <Typography>
                    <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-host-company">
                        <Typography component="span" variant="subtitle1">
                            Company:
                        </Typography>
                    </LocalizedStrict>{" "}
                    {companyName}
                </Typography>
            </Box>
            <Box className={classes.detailRow}>
                <FontAwesomeIcon icon={faClock} className={classes.icon} />
                <Typography>
                    <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-meeting-time">
                        <Typography component="span" variant="subtitle1">
                            Meeting time:
                        </Typography>
                    </LocalizedStrict>{" "}
                    {time}
                </Typography>
            </Box>
            <Box className={classes.detailRow}>
                <FontAwesomeIcon icon={faLocationDot} className={classes.icon} />
                <Typography>
                    <LocalizedStrict id="service-center-reports-dashboard-meetings-drawer-location">
                        <Typography component="span" variant="subtitle1">
                            Location:
                        </Typography>
                    </LocalizedStrict>{" "}
                    {location}
                </Typography>
            </Box>
        </Box>
    )
}
